import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    TextField, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Select, MenuItem, InputLabel, FormControl,
    Chip, Checkbox, FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, MoneyOff as RefundIcon } from '@mui/icons-material';
import SimpleDatePicker from '../../../../ui/simpleDatePicker';
import { formatAmountForDisplay, formatDateWithTimezone } from '../../../../../utils';
import { ValidDataService } from '../../../../../service/validDataService';

const RETURN_DISPOSITION_RESTOCK = 'restock';
const RETURN_DISPOSITION_WRITE_OFF = 'write_off';
const RETURN_DISPOSITION_NOT_RETURNABLE = 'not_returnable';
const RETURN_DISPOSITION_CANCEL_ORDER = 'cancel_order';
const RESTOCKING_FEE_DEDUCT_FROM_REFUND = 'deduct_from_refund';
const RESTOCKING_FEE_COLLECT_SEPARATELY = 'collect_separately';

const RETURN_DISPOSITION_LABELS = {
    [RETURN_DISPOSITION_RESTOCK]: 'Restock',
    [RETURN_DISPOSITION_WRITE_OFF]: 'Write Off',
    [RETURN_DISPOSITION_NOT_RETURNABLE]: 'Not returnable',
    [RETURN_DISPOSITION_CANCEL_ORDER]: 'Cancel order'
};

const isOrderNewFrameItem = (item) => {
    const normalizedType = String(item?.type || '').toLowerCase();
    const normalizedHandling = String(item?.frameHandling || '').toLowerCase();
    return normalizedType === 'frame' && normalizedHandling === 'order new';
};

const isLensOrContactItem = (item) => {
    const normalizedType = String(item?.type || '').toLowerCase();
    return normalizedType === 'lens' || normalizedType === 'contact_lens';
};

const getDefaultDispositionForItem = (item) => {
    if (isOrderNewFrameItem(item)) return RETURN_DISPOSITION_CANCEL_ORDER;
    if (isLensOrContactItem(item)) return RETURN_DISPOSITION_NOT_RETURNABLE;
    return RETURN_DISPOSITION_RESTOCK;
};

// Helper component for the Add/Edit Refund Dialog
function AddEditRefundDialog({ open, onClose, refundData, onSave, payments = [], refunds = [], maxRefundAmount = 0, invoiceItems = [], claims = [] }) {
    const [refundMethod, setRefundMethod] = useState('cash');
    const [refundMethodOverride, setRefundMethodOverride] = useState(false);
    const [refundAmount, setRefundAmount] = useState('');
    const [reason, setReason] = useState('');
    const [authorizedBy, setAuthorizedBy] = useState('');
    const [notes, setNotes] = useState('');
    const [selectedPayments, setSelectedPayments] = useState({});
    const [paymentAllocations, setPaymentAllocations] = useState({});
    const [refundDate, setRefundDate] = useState('');
    const [restockingFeeAmount, setRestockingFeeAmount] = useState('');
    const [restockingFeeHandlingMode, setRestockingFeeHandlingMode] = useState(RESTOCKING_FEE_DEDUCT_FROM_REFUND);
    const [feePaymentMethodId, setFeePaymentMethodId] = useState('');
    const [validPaymentMethods, setValidPaymentMethods] = useState([]);
    const [insuranceReversalNotes, setInsuranceReversalNotes] = useState('');
    const [selectedClaims, setSelectedClaims] = useState({});
    const [claimReversalAmounts, setClaimReversalAmounts] = useState({});
    const [errors, setErrors] = useState({});

    const eligibleReturnItems = useMemo(() => {
        return (invoiceItems || []).filter(item => !item._markedForDeletion);
    }, [invoiceItems]);

    const eligibleClaims = useMemo(() => {
        return (claims || []).filter(claim => !claim._markedForDeletion);
    }, [claims]);

    const activePayments = useMemo(() => {
        return (payments || []).filter(payment => !payment._markedForDeletion);
    }, [payments]);

    const paymentAvailability = useMemo(() => {
        const skipRefundId = refundData?.id || null;
        const skipTempId = refundData?.tempId || null;
        const byPaymentId = {};

        (refunds || [])
            .filter(refund => !refund?._markedForDeletion)
            .filter(refund => refund.id !== skipRefundId && refund.tempId !== skipTempId)
            .forEach(refund => {
                if (Array.isArray(refund.paymentAllocations) && refund.paymentAllocations.length > 0) {
                    refund.paymentAllocations.forEach(alloc => {
                        const key = String(alloc.paymentId);
                        byPaymentId[key] = (byPaymentId[key] || 0) + (parseFloat(alloc.amount) || 0);
                    });
                    return;
                }

                if (refund.paymentId) {
                    const key = String(refund.paymentId);
                    byPaymentId[key] = (byPaymentId[key] || 0) + (parseFloat(refund.refundAmount) || 0);
                }
            });

        const availability = {};
        activePayments.forEach(payment => {
            const key = String(payment.id);
            const paid = parseFloat(payment.paymentAmount) || 0;
            const alreadyRefunded = byPaymentId[key] || 0;
            availability[key] = Math.max(0, paid - alreadyRefunded);
        });

        return availability;
    }, [activePayments, refunds, refundData]);

    const hasActivePayments = activePayments.length > 0;
    const allocatedTotal = useMemo(() => {
        return Object.keys(selectedPayments)
            .filter(key => selectedPayments[key])
            .reduce((sum, key) => sum + (parseFloat(paymentAllocations[key]) || 0), 0);
    }, [selectedPayments, paymentAllocations]);

    const normalizedRestockingFee = useMemo(() => {
        return Math.max(0, parseFloat(restockingFeeAmount) || 0);
    }, [restockingFeeAmount]);

    const grossRefundAmount = useMemo(() => {
        return hasActivePayments ? allocatedTotal : (parseFloat(refundAmount) || 0);
    }, [hasActivePayments, allocatedTotal, refundAmount]);

    const netRefundAmount = useMemo(() => {
        if (restockingFeeHandlingMode === RESTOCKING_FEE_COLLECT_SEPARATELY) {
            return grossRefundAmount;
        }
        return Math.max(0, grossRefundAmount - normalizedRestockingFee);
    }, [grossRefundAmount, normalizedRestockingFee, restockingFeeHandlingMode]);

    useEffect(() => {
        if (!open) return;

        let active = true;

        (async () => {
            try {
                const response = await ValidDataService.getValidPaymentMethods();
                if (!active) return;
                if (response?.code === 0) {
                    setValidPaymentMethods(response.data || []);
                }
            } catch (_error) {
                if (active) {
                    setValidPaymentMethods([]);
                }
            }
        })();

        return () => {
            active = false;
        };
    }, [open]);

    const selectedReturnLines = useMemo(() => {
        return eligibleReturnItems.map(item => ({
            sourceInvoiceItemId: item.id,
            quantity: Math.max(parseInt(item.quantity, 10) || 0, 0),
            unitPrice: Math.max(0, parseFloat(item.price) || 0),
            returnDisposition: getDefaultDispositionForItem(item),
        }));
    }, [eligibleReturnItems]);

    /* TODO: needs to match what we have on payment side */
    const refundMethods = [
        { value: 'cash', label: 'Cash' },
        { value: 'credit', label: 'Credit Card' },
        { value: 'debit', label: 'Debit Card' },
        { value: 'check', label: 'Check' },
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'other', label: 'Other' }
    ];

    // Populate form when refundData changes (for editing)
    useEffect(() => {
        if (open && refundData) {
            setRefundAmount(refundData.refundAmount || '');
            setReason(refundData.reason || '');
            setRefundMethod(refundData.refundMethod || 'cash');
            setRefundMethodOverride(!!refundData.refundMethodOverride);
            setAuthorizedBy(refundData.authorizedBy || '');
            setNotes(refundData.notes || '');
            setRefundDate(refundData.refundDate ? formatDateWithTimezone(refundData.refundDate) : new Date());
            setRestockingFeeAmount(refundData?.returnInvoice?.restockingFeeAmount || '');
            setRestockingFeeHandlingMode(refundData?.returnInvoice?.restockingFeeHandlingMode || RESTOCKING_FEE_DEDUCT_FROM_REFUND);
            setFeePaymentMethodId(refundData?.returnInvoice?.restockingFeePaymentMethodId || '');
            setInsuranceReversalNotes(refundData?.returnInvoice?.insuranceReversalNotes || '');

            const nextSelected = {};
            const nextAllocations = {};

            if (Array.isArray(refundData.paymentAllocations) && refundData.paymentAllocations.length > 0) {
                refundData.paymentAllocations.forEach(alloc => {
                    const key = String(alloc.paymentId);
                    nextSelected[key] = true;
                    nextAllocations[key] = String(parseFloat(alloc.amount || 0));
                });
            } else if (refundData.paymentId) {
                const key = String(refundData.paymentId);
                nextSelected[key] = true;
                nextAllocations[key] = String(parseFloat(refundData.refundAmount || 0));
            }

            setSelectedPayments(nextSelected);
            setPaymentAllocations(nextAllocations);
            setSelectedClaims({});
            setClaimReversalAmounts({});
        } else if (open) {
            // Reset form for new refund
            setRefundAmount('');
            setReason('');
            setRefundMethod('cash');
            setRefundMethodOverride(false);
            setAuthorizedBy('');
            setNotes('');
            setRefundDate('');
            setSelectedPayments({});
            setPaymentAllocations({});
            setRestockingFeeAmount('');
            setRestockingFeeHandlingMode(RESTOCKING_FEE_DEDUCT_FROM_REFUND);
            setFeePaymentMethodId('');
            setInsuranceReversalNotes('');

            const nextClaimSelection = {};
            const nextClaimAmounts = {};
            eligibleClaims.forEach(claim => {
                const baseAmount = parseFloat(claim.approvedAmount ?? claim.claimAmount ?? 0) || 0;
                if (baseAmount > 0) {
                    nextClaimSelection[claim.id] = true;
                    nextClaimAmounts[claim.id] = String(baseAmount.toFixed(2));
                }
            });
            setSelectedClaims(nextClaimSelection);
            setClaimReversalAmounts(nextClaimAmounts);
        }
        setErrors({}); // Clear errors on open/data change
    }, [open, refundData, eligibleReturnItems, eligibleClaims]);

    // Validate form data
    const validate = () => {
        const newErrors = {};

        if (!grossRefundAmount || isNaN(grossRefundAmount) || parseFloat(grossRefundAmount) <= 0) {
            newErrors.refundAmount = 'Valid refund amount is required';
        } else if (parseFloat(grossRefundAmount) > maxRefundAmount) {
            newErrors.refundAmount = `Refund amount cannot exceed $${maxRefundAmount.toFixed(2)}`;
        }
        
        if (normalizedRestockingFee > 0 && restockingFeeHandlingMode === RESTOCKING_FEE_DEDUCT_FROM_REFUND && normalizedRestockingFee > grossRefundAmount) {
            newErrors.restockingFeeAmount = `Restocking fee ($${normalizedRestockingFee.toFixed(2)}) cannot exceed the refund amount ($${grossRefundAmount.toFixed(2)}).`;
        }

        if (
            normalizedRestockingFee > 0
            && restockingFeeHandlingMode === RESTOCKING_FEE_COLLECT_SEPARATELY
            && !feePaymentMethodId
        ) {
            newErrors.feePaymentMethodId = 'Select how the restocking fee was collected';
        }

        if (!reason || reason.trim().length < 3) {
            newErrors.reason = 'Reason is required (minimum 3 characters)';
        }

        if (!refundDate) {
            newErrors.refundDate = 'Refund date is required';
        }

        if (hasActivePayments) {
            const selectedIds = Object.keys(selectedPayments).filter(key => selectedPayments[key]);
            if (selectedIds.length === 0) {
                newErrors.paymentAllocations = 'Select at least one related payment';
            } else {
                let allocationTotal = 0;
                for (const paymentKey of selectedIds) {
                    const allocated = parseFloat(paymentAllocations[paymentKey]);
                    const available = parseFloat(paymentAvailability[paymentKey] || 0);

                    if (!allocated || allocated <= 0) {
                        newErrors.paymentAllocations = 'Each selected payment must have a refund amount greater than 0';
                        break;
                    }

                    if (allocated > available) {
                        newErrors.paymentAllocations = `Allocation for a selected payment exceeds available refundable amount`;
                        break;
                    }

                    allocationTotal += allocated;
                }

                if (!newErrors.paymentAllocations) {
                    if (Math.abs(allocationTotal - grossRefundAmount) > 0.01) {
                        newErrors.paymentAllocations = 'Allocation total and refund total must match';
                    }
                }
            }
        }

        if (selectedReturnLines.length === 0) {
            newErrors.returnItems = 'No source invoice items found to mirror on the return invoice.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleClaimToggle = (claimId) => {
        const key = String(claimId);
        setSelectedClaims(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleClaimAmountChange = (claimId, value) => {
        const key = String(claimId);
        setClaimReversalAmounts(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handlePaymentToggle = (paymentId) => {
        const key = String(paymentId);
        const checked = !selectedPayments[key];
        const available = parseFloat(paymentAvailability[key] || 0);

        setSelectedPayments(prev => ({
            ...prev,
            [key]: checked
        }));

        setPaymentAllocations(prev => ({
            ...prev,
            [key]: checked ? (prev[key] || String(available.toFixed(2))) : ''
        }));
    };

    const handleAllocationChange = (paymentId, value) => {
        const key = String(paymentId);
        setPaymentAllocations(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

    const buildNetPaymentAllocations = (rawAllocations, feeAmount) => {
        let feeRemaining = roundMoney(feeAmount);

        return rawAllocations
            .map(alloc => {
                const amount = roundMoney(alloc.amount);
                const deduction = Math.min(feeRemaining, amount);
                feeRemaining = roundMoney(feeRemaining - deduction);

                return {
                    ...alloc,
                    amount: roundMoney(amount - deduction)
                };
            })
            .filter(alloc => alloc.amount > 0);
    };

    const handleSave = async () => {
        if (!validate()) return;

        const selectedPaymentAllocations = Object.keys(selectedPayments)
            .filter(key => selectedPayments[key])
            .map(key => ({
                paymentId: Number(key),
                amount: parseFloat(paymentAllocations[key] || 0)
            }))
            .filter(alloc => alloc.amount > 0);

        const refundPaymentAllocations = hasActivePayments
            ? (restockingFeeHandlingMode === RESTOCKING_FEE_COLLECT_SEPARATELY
                ? selectedPaymentAllocations
                : buildNetPaymentAllocations(selectedPaymentAllocations, normalizedRestockingFee))
            : [];

        const refundDataToSave = {
            refundAmount: roundMoney(netRefundAmount),
            reason: reason.trim(),
            refundMethod: hasActivePayments
                ? (refundMethodOverride ? refundMethod : null)
                : refundMethod,
            refundMethodOverride,
            authorizedBy: authorizedBy.trim() || null,
            notes: notes.trim() || null,
            refundDate: refundDate, // Already in YYYY-MM-DD format
            status: refundData?.status || 'approved', // Ensure status is preserved or set to default
            paymentAllocations: refundPaymentAllocations
        };

        if (refundDataToSave.paymentAllocations.length === 1) {
            refundDataToSave.paymentId = refundDataToSave.paymentAllocations[0].paymentId;
        } else {
            refundDataToSave.paymentId = null;
        }

        if (refundData) {
            // Editing existing refund (persist tempId and staged returnInvoice payload if present)
            const insuranceReversals = eligibleClaims
                .filter(claim => selectedClaims[String(claim.id)])
                .map(claim => ({
                    sourceClaimId: claim.id,
                    reversalAmount: parseFloat(claimReversalAmounts[String(claim.id)] || 0),
                    notes: insuranceReversalNotes || null,
                }))
                .filter(reversal => reversal.reversalAmount > 0);

            const mergedReturnInvoice = refundData?.returnInvoice
                ? {
                    ...refundData.returnInvoice,
                    returnReason: reason.trim(),
                    notes: notes.trim() || null,
                    invoiceDate: refundDate || null,
                    restockingFeeAmount: parseFloat(restockingFeeAmount || 0) || 0,
                    restockingFeeHandlingMode,
                    restockingFeePaymentMethodId: feePaymentMethodId ? Number(feePaymentMethodId) : null,
                    restockingFeePaymentDate: refundDate || null,
                    restockingFeeDescription: 'Restocking fee',
                    insuranceReversalNotes: insuranceReversalNotes.trim() || null,
                    insuranceReversals,
                    items: selectedReturnLines.length > 0
                        ? selectedReturnLines
                        : (refundData.returnInvoice.items || [])
                }
                : null;

            const editedPayload = {
                ...refundData,
                ...refundDataToSave,
                id: refundData.id,
                tempId: refundData.tempId,
                returnInvoice: mergedReturnInvoice || refundData.returnInvoice || null
            };

            const result = await onSave(editedPayload);
            if (result !== false) {
                onClose();
            }
            return;
        }

        const insuranceReversals = eligibleClaims
            .filter(claim => selectedClaims[String(claim.id)])
            .map(claim => ({
                sourceClaimId: claim.id,
                reversalAmount: parseFloat(claimReversalAmounts[String(claim.id)] || 0),
                notes: insuranceReversalNotes || null,
            }))
            .filter(reversal => reversal.reversalAmount > 0);

        const unifiedPayload = {
            refund: refundDataToSave,
            returnInvoice: {
                returnReason: reason.trim(),
                notes: notes.trim() || null,
                invoiceDate: refundDate || null,
                restockingFeeAmount: parseFloat(restockingFeeAmount || 0) || 0,
                restockingFeeHandlingMode,
                restockingFeePaymentMethodId: feePaymentMethodId ? Number(feePaymentMethodId) : null,
                restockingFeePaymentDate: refundDate || null,
                restockingFeeDescription: 'Restocking fee',
                insuranceReversalNotes: insuranceReversalNotes.trim() || null,
                insuranceReversals,
                items: selectedReturnLines
            }
        };

        const result = await onSave(unifiedPayload);
        if (result !== false) {
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {refundData ? 'Edit Refund' : 'Process Refund'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        This single flow stages both the patient refund and return invoice. They are created only when you click Save on the main invoice dialog, giving you time to review and correct values first.
                    </Typography>

                    {/* Related Payments and allocations */}
                    {activePayments.length > 0 && (
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Related Payments (required)
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox">Use</TableCell>
                                            <TableCell>Payment</TableCell>
                                            <TableCell align="right">Available</TableCell>
                                            <TableCell align="right">Refund This Payment</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {activePayments.map((payment) => {
                                            const key = String(payment.id);
                                            const checked = !!selectedPayments[key];
                                            const available = parseFloat(paymentAvailability[key] || 0);
                                            return (
                                                <TableRow key={`payment-allocation-${payment.id}`}>
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            checked={checked}
                                                            onChange={() => handlePaymentToggle(payment.id)}
                                                            disabled={available <= 0}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {formatDateWithTimezone(payment.paymentDate)} - ${parseFloat(payment.paymentAmount || 0).toFixed(2)}
                                                        {payment.paymentMethod && ` (${payment.paymentMethod.name})`}
                                                    </TableCell>
                                                    <TableCell align="right">${available.toFixed(2)}</TableCell>
                                                    <TableCell align="right" sx={{ width: 180 }}>
                                                        <TextField
                                                            size="small"
                                                            type="text"
                                                            value={paymentAllocations[key] || ''}
                                                            onChange={(e) => handleAllocationChange(payment.id, e.target.value)}
                                                            disabled={!checked}
                                                            inputProps={{ min: '0', step: '0.01', max: available }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {errors.paymentAllocations && (
                                <Typography variant="caption" color="error">
                                    {errors.paymentAllocations}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Refund Amount */}
                    {hasActivePayments ? (
                        <TextField
                            label="Net Refund Amount (auto-calculated)"
                            value={netRefundAmount.toFixed(2)}
                            error={!!errors.refundAmount}
                            helperText={errors.refundAmount || `Gross allocations: $${allocatedTotal.toFixed(2)} - Restocking fee: $${normalizedRestockingFee.toFixed(2)} (Maximum gross: $${maxRefundAmount.toFixed(2)})`}
                            fullWidth
                            InputProps={{ readOnly: true }}
                        />
                    ) : (
                        <TextField
                            label="Refund Amount (before fee deduction)"
                            type="number"
                            value={refundAmount}
                            onChange={(e) => setRefundAmount(e.target.value)}
                            error={!!errors.refundAmount}
                            helperText={errors.refundAmount || `Net refund after fee: $${netRefundAmount.toFixed(2)} (Maximum gross: $${maxRefundAmount.toFixed(2)})`}
                            fullWidth
                            required
                            inputProps={{ step: '0.01', min: '0' }}
                        />
                    )}

                    {/* Reason */}
                    <TextField
                        label="Reason for Refund"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        error={!!errors.reason}
                        helperText={errors.reason}
                        fullWidth
                        required
                        multiline
                        rows={3}
                        placeholder="Please provide a detailed reason for the refund..."
                    />

                    {/* Refund Method */}
                    {hasActivePayments ? (
                        <>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={refundMethodOverride}
                                        onChange={(e) => setRefundMethodOverride(e.target.checked)}
                                    />
                                }
                                label="Use one refund method for all selected payments"
                            />
                            {refundMethodOverride ? (
                                <FormControl fullWidth>
                                    <InputLabel>Override Refund Method</InputLabel>
                                    <Select
                                        value={refundMethod}
                                        onChange={(e) => setRefundMethod(e.target.value)}
                                        label="Override Refund Method"
                                    >
                                        {refundMethods.map((method, index) => (
                                            <MenuItem key={`refund-method-${method.value}-${index}`} value={method.value}>
                                                {method.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            ) : (
                                <Typography variant="caption" color="textSecondary">
                                    Refund method will be inferred from each selected payment method.
                                </Typography>
                            )}
                        </>
                    ) : (
                        <FormControl fullWidth>
                            <InputLabel>Refund Method</InputLabel>
                            <Select
                                value={refundMethod}
                                onChange={(e) => setRefundMethod(e.target.value)}
                                label="Refund Method"
                            >
                                {refundMethods.map((method, index) => (
                                    <MenuItem key={`refund-method-${method.value}-${index}`} value={method.value}>
                                        {method.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* Refund Date */}
                    <SimpleDatePicker
                        label="Refund Date"
                        value={refundDate}
                        onChange={setRefundDate}
                        error={!!errors.refundDate}
                        helperText={errors.refundDate}
                        fullWidth
                        required
                    />

                    {/* Authorized By */}
                    <TextField
                        label="Authorized By"
                        value={authorizedBy}
                        onChange={(e) => setAuthorizedBy(e.target.value)}
                        fullWidth
                        placeholder="Manager name or authorization code"
                    />

                    {/* Notes */}
                    {/* <TextField
                        label="Additional Notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Any additional notes about this refund..."
                    /> */}

                    <Typography variant="subtitle2" sx={{ mt: 1 }}>
                        Return Items (required)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Return invoice line items are mirrored automatically from the source invoice as negative entries.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        Line-level quantity, price, and disposition edits are disabled in this workflow to keep source and return invoices fully offsetting.
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Item</TableCell>
                                    <TableCell align="right">Sold Qty</TableCell>
                                    <TableCell align="right">Sold Unit Price</TableCell>
                                    <TableCell align="right">Return Qty</TableCell>
                                    <TableCell align="right">Return Unit Price</TableCell>
                                    <TableCell>Disposition</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {eligibleReturnItems.map(item => {
                                    const disposition = getDefaultDispositionForItem(item);
                                    const returnQty = Math.max(parseInt(item.quantity, 10) || 0, 0);
                                    const returnUnitPrice = Math.max(0, parseFloat(item.price) || 0);

                                    return (
                                    <TableRow key={`return-item-${item.id}`}>
                                        <TableCell>{item.description}</TableCell>
                                        <TableCell align="right">{item.quantity || 0}</TableCell>
                                        <TableCell align="right">${parseFloat(item.price || 0).toFixed(2)}</TableCell>
                                        <TableCell align="right">{returnQty}</TableCell>
                                        <TableCell align="right">${returnUnitPrice.toFixed(2)}</TableCell>
                                        <TableCell>{RETURN_DISPOSITION_LABELS[disposition] || disposition}</TableCell>
                                    </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {errors.returnItems && (
                        <Typography variant="caption" color="error">
                            {errors.returnItems}
                        </Typography>
                    )}

                    <TextField
                        label="Restocking Fee (optional)"
                        type="text"
                        value={restockingFeeAmount}
                        onChange={(e) => setRestockingFeeAmount(e.target.value)}
                        error={!!errors.restockingFeeAmount}
                        helperText={errors.restockingFeeAmount || 'Deducted from refund payout and added as a fee line on the return invoice'}
                        fullWidth
                    />

                    {normalizedRestockingFee > 0 && (
                        <FormControl fullWidth error={!!errors.feeHandlingMode}>
                            <InputLabel>Restocking Fee Handling</InputLabel>
                            <Select
                                value={restockingFeeHandlingMode}
                                onChange={(e) => setRestockingFeeHandlingMode(e.target.value)}
                                label="Restocking Fee Handling"
                            >
                                <MenuItem value={RESTOCKING_FEE_DEDUCT_FROM_REFUND}>Deduct from refund payout</MenuItem>
                                <MenuItem value={RESTOCKING_FEE_COLLECT_SEPARATELY}>Collect separately</MenuItem>
                            </Select>
                        </FormControl>
                    )}

                    {normalizedRestockingFee > 0 && restockingFeeHandlingMode === RESTOCKING_FEE_COLLECT_SEPARATELY && (
                        <FormControl fullWidth error={!!errors.feePaymentMethodId}>
                            <InputLabel>Fee Collection Method</InputLabel>
                            <Select
                                value={feePaymentMethodId}
                                onChange={(e) => setFeePaymentMethodId(e.target.value)}
                                label="Fee Collection Method"
                            >
                                <MenuItem value=""><em>Select Method</em></MenuItem>
                                {validPaymentMethods.map((method) => (
                                    <MenuItem key={`fee-method-${method.id}`} value={method.id}>{method.name}</MenuItem>
                                ))}
                            </Select>
                            {errors.feePaymentMethodId && (
                                <Typography variant="caption" color="error">
                                    {errors.feePaymentMethodId}
                                </Typography>
                            )}
                        </FormControl>
                    )}

                    <Typography variant="body2" color="text.secondary">
                        {restockingFeeHandlingMode === RESTOCKING_FEE_COLLECT_SEPARATELY
                            ? `Refund to customer: $${netRefundAmount.toFixed(2)}`
                            : `Net refund to customer: $${netRefundAmount.toFixed(2)}`}
                        {normalizedRestockingFee > 0 && restockingFeeHandlingMode === RESTOCKING_FEE_DEDUCT_FROM_REFUND
                            ? ` ($${grossRefundAmount.toFixed(2)} - $${normalizedRestockingFee.toFixed(2)} fee)`
                            : ''}
                        {normalizedRestockingFee > 0 && restockingFeeHandlingMode === RESTOCKING_FEE_COLLECT_SEPARATELY
                            ? ` | Restocking fee collected separately: $${normalizedRestockingFee.toFixed(2)}`
                            : ''}
                    </Typography>

                    {eligibleClaims.length > 0 && (
                        <>
                            <Typography variant="subtitle2">Insurance Reversal Ledger</Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox">Reverse</TableCell>
                                            <TableCell>Provider</TableCell>
                                            <TableCell align="right">Available</TableCell>
                                            <TableCell align="right">Reversal Amount</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {eligibleClaims.map(claim => {
                                            const key = String(claim.id);
                                            const available = parseFloat(claim.approvedAmount ?? claim.claimAmount ?? 0) || 0;
                                            const checked = !!selectedClaims[key];
                                            return (
                                                <TableRow key={`claim-reversal-${claim.id}`}>
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            checked={checked}
                                                            onChange={() => handleClaimToggle(claim.id)}
                                                            disabled={available <= 0}
                                                        />
                                                    </TableCell>
                                                    <TableCell>{claim.provider || 'Insurance'}</TableCell>
                                                    <TableCell align="right">${available.toFixed(2)}</TableCell>
                                                    <TableCell align="right" sx={{ width: 180 }}>
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            value={claimReversalAmounts[key] || ''}
                                                            onChange={(e) => handleClaimAmountChange(claim.id, e.target.value)}
                                                            disabled={!checked}
                                                            inputProps={{ min: '0', step: '0.01', max: available }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TextField
                                label="Insurance Reversal Notes"
                                value={insuranceReversalNotes}
                                onChange={(e) => setInsuranceReversalNotes(e.target.value)}
                                fullWidth
                                multiline
                                rows={2}
                                placeholder="Optional notes for insurance portal reversal/audit trail"
                            />
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained" color="primary">
                    {refundData ? 'Update Refund' : 'Process Refund'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// Main RefundCollector component
const RefundCollector = ({ 
    refunds = [], 
    onAddRefund, 
    onEditRefund, 
    onDeleteRefund, 
    onUpdateRefundStatus,
    disabled = false,
    payments = [],
    maxRefundAmount = 0,
    invoiceItems = [],
    claims = []
}) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingRefund, setEditingRefund] = useState(null);

    
    const totalRefunded = useMemo(() => {
        return refunds
            .filter(refund => refund.status === 'approved' || refund.status === 'processed')
            .reduce((sum, refund) => sum + parseFloat(refund.refundAmount), 0);
    }, [refunds]);

    const handleAddClick = () => {
        setEditingRefund(null);
        setDialogOpen(true);
    };

    const handleEditClick = (refund) => {
        setEditingRefund(refund);
        setDialogOpen(true);
    };

    const handleSave = async (refundData) => {
        if (editingRefund) {
            return onEditRefund(refundData);
        }
        return onAddRefund(refundData);
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RefundIcon />
                    Refunds
                    {totalRefunded > 0 && (
                        <Chip 
                            label={`Total: ${formatAmountForDisplay(totalRefunded)}`} 
                            color="error" 
                            size="small" 
                        />
                    )}
                </Typography>
                <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddClick}
                    variant="outlined"
                    color="error"
                    size="small"
                    disabled={disabled || maxRefundAmount <= 0}
                >
                    Process Refund
                </Button>
            </Box>

            {refunds.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
                    No refunds processed
                </Typography>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Method</TableCell>
                                <TableCell>Reason</TableCell>
                                {/* <TableCell>Status</TableCell> */}
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {refunds.map((refund) => (
                                <TableRow 
                                    key={refund.id || refund.tempId}
                                    sx={{
                                        opacity: refund._markedForDeletion ? 0.4 : 1,
                                        textDecoration: refund._markedForDeletion ? 'line-through' : 'none'
                                    }}
                                >
                                    <TableCell>
                                        {refund._markedForDeletion && (
                                            <Typography
                                                component="span"
                                                sx={{
                                                    color: '#fff',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 'bold',
                                                    mr: 1,
                                                    px: 0.5,
                                                    py: 0.25,
                                                    backgroundColor: '#d32f2f',
                                                    borderRadius: 0.5,
                                                    display: 'inline-block',
                                                    opacity: 1
                                                }}
                                            >
                                                MARKED FOR DELETION
                                            </Typography>
                                        )}
                                        {formatDateWithTimezone(refund.refundDate)}
                                    </TableCell>
                                    <TableCell>
                                        <Typography color="error" fontWeight="bold">
                                            -{formatAmountForDisplay(refund.refundAmount)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {refund.refundMethod?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </TableCell>
                                    <TableCell>
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                maxWidth: 200, 
                                                overflow: 'hidden', 
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                            title={refund.reason}
                                        >
                                            {refund.reason}
                                        </Typography>
                                    </TableCell>
                                    {/* <TableCell>
                                        <RefundStatusChip status={refund.status} />
                                    </TableCell> */}
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            {/* {refund.status === 'pending' && ( */}
                                                <>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEditClick(refund)}
                                                        disabled={disabled}
                                                        title="Edit refund"
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    {/* <IconButton
                                                        size="small"
                                                        onClick={() => handleStatusUpdate(refund.id, 'approved')}
                                                        disabled={disabled}
                                                        color="success"
                                                        title="Approve refund"
                                                    >
                                                        ✓
                                                    </IconButton> */}
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onDeleteRefund(refund.id || refund.tempId)}
                                                        disabled={disabled}
                                                        color="error"
                                                        title="Delete refund"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </>
                                            {/* )} */}
                                            {/* <IconButton
                                                size="small"
                                                onClick={() => onDeleteRefund(refund.id || refund.tempId)}
                                                disabled={disabled}
                                                color="error"
                                                title="Delete refund"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>   */}
                                            {/* {refund.status === 'approved' && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleStatusUpdate(refund.id, 'processed')}
                                                    disabled={disabled}
                                                    color="primary"
                                                    title="Mark as processed"
                                                >
                                                    ✓✓
                                                </IconButton>
                                            )} */}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <AddEditRefundDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                refundData={editingRefund}
                onSave={handleSave}
                payments={payments}
                refunds={refunds}
                maxRefundAmount={maxRefundAmount}
                invoiceItems={invoiceItems}
                claims={claims}
            />
        </Box>
    );
};

export default RefundCollector;
